/**
 * The document formatter.
 *
 * Printing is driven by the AST rather than the token stream, which makes it
 * impossible for the formatter to change what the code means: whatever comes
 * out re-parses to the same tree. Comments are the one piece of trivia the AST
 * does not carry, so they are re-attached by source position — each comment is
 * emitted just before the first statement that starts after it.
 *
 * If the document has syntax errors the formatter declines and returns the
 * source unchanged. Re-printing a tree that error recovery has patched up could
 * silently drop the very text the user is in the middle of typing.
 */
import type {
  Block,
  FunctionDeclaration,
  IfStatement,
  Label,
  Program,
  Statement,
  SwitchCase,
  TopLevelStatement,
} from '../ast/nodes';
import type { Token } from '../tokenizer/tokens';
import { parse } from '../parser/parser';
import { hasErrors } from '../diagnostics/types';

export interface FormatOptions {
  /** Spaces per indent level. */
  readonly indentSize: number;
  /** Use a tab character instead of spaces. */
  readonly useTabs: boolean;
  /**
   * `allman` puts `{` on its own line (the style used throughout the docs and
   * examples); `same-line` keeps it on the header line.
   */
  readonly braceStyle: 'allman' | 'same-line';
  /** Blank lines between top-level declarations and statements groups. */
  readonly blankLineBetweenFunctions: boolean;
  readonly endWithNewline: boolean;
}

export const DEFAULT_FORMAT_OPTIONS: FormatOptions = {
  indentSize: 4,
  useTabs: false,
  braceStyle: 'allman',
  blankLineBetweenFunctions: true,
  endWithNewline: true,
};

export interface FormatResult {
  readonly text: string;
  /** False when the document had syntax errors and was returned untouched. */
  readonly formatted: boolean;
  readonly reason?: 'syntax-errors';
}

export function formatDocument(source: string, options: Partial<FormatOptions> = {}): FormatResult {
  const opts = { ...DEFAULT_FORMAT_OPTIONS, ...options };
  const { program, diagnostics, tokens } = parse(source);

  if (hasErrors(diagnostics)) {
    return { text: source, formatted: false, reason: 'syntax-errors' };
  }

  const printer = new Printer(opts, collectComments(tokens));
  const text = printer.printProgram(program);
  return { text, formatted: true };
}

interface Comment {
  readonly text: string;
  readonly offset: number;
  readonly ownLine: boolean;
  readonly block: boolean;
}

function collectComments(tokens: readonly Token[]): Comment[] {
  const comments: Comment[] = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i]!;
    if (token.type !== 'line-comment' && token.type !== 'block-comment') continue;
    const previous = tokens[i - 1];
    comments.push({
      text: token.value,
      offset: token.range.start.offset,
      // A comment is "own line" when nothing precedes it on that line.
      ownLine: previous === undefined || token.precededByNewline,
      block: token.type === 'block-comment',
    });
  }
  return comments;
}

class Printer {
  private readonly lines: string[] = [];
  private commentIndex = 0;

  constructor(
    private readonly options: FormatOptions,
    private readonly comments: readonly Comment[],
  ) {}

  printProgram(program: Program): string {
    program.body.forEach((item, index) => {
      this.emitCommentsBefore(item.range.start.offset, 0);
      if (
        index > 0 &&
        this.options.blankLineBetweenFunctions &&
        this.needsBlankLine(program.body, index)
      ) {
        this.blank();
      }
      this.printTopLevel(item, 0);
    });

    this.flushRemainingComments(0);
    this.trimTrailingBlanks();

    const text = this.lines.join('\n');
    return this.options.endWithNewline && text.length > 0 ? `${text}\n` : text;
  }

  /** A blank line goes around function declarations and after a block closes. */
  private needsBlankLine(body: readonly TopLevelStatement[], index: number): boolean {
    const previous = body[index - 1]!;
    const current = body[index]!;
    return (
      previous.kind === 'FunctionDeclaration' ||
      current.kind === 'FunctionDeclaration' ||
      isCompound(previous) ||
      isCompound(current)
    );
  }

  /* --- emitting ---------------------------------------------------- */

  private indent(level: number): string {
    return this.options.useTabs ? '\t'.repeat(level) : ' '.repeat(level * this.options.indentSize);
  }

  private line(level: number, text: string): void {
    this.lines.push(text === '' ? '' : this.indent(level) + text);
  }

  private blank(): void {
    if (this.lines.length > 0 && this.lines[this.lines.length - 1] !== '') {
      this.lines.push('');
    }
  }

  private trimTrailingBlanks(): void {
    while (this.lines.length > 0 && this.lines[this.lines.length - 1] === '') this.lines.pop();
  }

  /** Emits every not-yet-emitted comment that starts before `offset`. */
  private emitCommentsBefore(offset: number, level: number): void {
    while (this.commentIndex < this.comments.length) {
      const comment = this.comments[this.commentIndex]!;
      if (comment.offset >= offset) break;
      this.commentIndex += 1;
      this.printComment(comment, level);
    }
  }

  private flushRemainingComments(level: number): void {
    while (this.commentIndex < this.comments.length) {
      this.printComment(this.comments[this.commentIndex]!, level);
      this.commentIndex += 1;
    }
  }

  private printComment(comment: Comment, level: number): void {
    if (!comment.block) {
      this.line(level, comment.text.trimEnd());
      return;
    }
    // Re-indent a block comment's continuation lines, keeping their relative shape.
    const raw = comment.text.split(/\r?\n/);
    const first = raw[0]!.trimEnd();
    this.line(level, first);
    for (const rest of raw.slice(1)) {
      this.line(level, ` ${rest.trim()}`);
    }
  }

  /* --- declarations ------------------------------------------------ */

  private printTopLevel(item: TopLevelStatement, level: number): void {
    if (item.kind === 'FunctionDeclaration') this.printFunction(item, level);
    else this.printStatement(item, level);
  }

  private printFunction(declaration: FunctionDeclaration, level: number): void {
    this.printAnnotations(declaration, level);
    const params =
      declaration.parameters === null
        ? ''
        : `(${declaration.parameters.map((p) => p.name).join(', ')})`;
    const header = `function ${declaration.name.text}${params}`;
    this.printBlockWithHeader(header, declaration.body, level);
  }

  private printAnnotations(
    node: { annotations: readonly { rawName: string; args: readonly string[] }[] },
    level: number,
  ): void {
    for (const annotation of node.annotations) {
      const args = annotation.args.map((a) => `"${escapeString(a)}"`).join(', ');
      this.line(level, args ? `@${annotation.rawName}(${args})` : `@${annotation.rawName}`);
    }
  }

  /* --- statements --------------------------------------------------- */

  private printStatement(statement: Statement, level: number): void {
    this.emitCommentsBefore(statement.range.start.offset, level);
    this.printAnnotations(statement, level);

    switch (statement.kind) {
      case 'ActionStatement':
        this.line(level, `${statement.label.text};`);
        return;

      // Terminators keep the keyword exactly as typed: `Start app;` is a
      // sentence the user wrote, and lowercasing it would change the label.
      case 'StartStatement':
      case 'EndStatement':
        this.line(
          level,
          statement.label
            ? `${statement.keyword} ${statement.label.text};`
            : `${statement.keyword};`,
        );
        return;

      case 'CallStatement': {
        const args =
          statement.args === null ? '' : `(${statement.args.map((a) => a.text).join(', ')})`;
        this.line(level, `call ${statement.callee.text}${args};`);
        return;
      }

      case 'ReturnStatement':
        this.line(level, statement.label ? `return ${statement.label.text};` : 'return;');
        return;

      case 'BreakStatement':
        this.line(level, 'break;');
        return;

      case 'ContinueStatement':
        this.line(level, 'continue;');
        return;

      case 'IfStatement':
        this.printIf(statement, level);
        return;

      case 'WhileStatement':
        this.printBlockWithHeader(
          `while (${statement.condition.text})${branchSuffix(statement.branchLabels)}`,
          statement.body,
          level,
        );
        return;

      case 'ForEachStatement':
        this.printBlockWithHeader(
          `for each (${statement.header.text})${branchSuffix(statement.branchLabels)}`,
          statement.body,
          level,
        );
        return;

      case 'RepeatStatement': {
        this.printBlockWithHeader('repeat', statement.body, level, {
          trailer: `until (${statement.condition.text})${branchSuffix(statement.branchLabels)};`,
        });
        return;
      }

      case 'SwitchStatement': {
        this.line(level, `switch (${statement.discriminant.text})`);
        this.openBrace(level);
        statement.cases.forEach((branch, index) => {
          if (index > 0) this.blank();
          this.printSwitchCase(branch, level + 1);
        });
        this.closeBrace(level);
        return;
      }

      case 'ErrorStatement':
        // Only reachable for statements the parser recovered from without
        // raising an error (an empty label, for instance). Keep the raw text.
        this.line(level, statement.text.trim());
        return;
    }
  }

  private printIf(statement: IfStatement, level: number): void {
    const header = `if (${statement.condition.text})${branchSuffix(statement.branchLabels)}`;
    this.printBlockWithHeader(header, statement.consequent, level, {
      suppressTrailingNewline: true,
    });

    const alternate = statement.alternate;
    if (!alternate) return;

    if (alternate.kind === 'IfStatement') {
      const chained = `else if (${alternate.condition.text})${branchSuffix(alternate.branchLabels)}`;
      this.printBlockWithHeader(chained, alternate.consequent, level, {
        suppressTrailingNewline: true,
      });
      if (alternate.alternate) {
        this.printElseTail(alternate.alternate, level);
      }
      return;
    }

    this.printElseTail(alternate, level);
  }

  private printElseTail(alternate: Block | IfStatement, level: number): void {
    if (alternate.kind === 'IfStatement') {
      this.printIf(alternate, level);
      return;
    }
    this.printBlockWithHeader('else', alternate, level);
  }

  private printSwitchCase(branch: SwitchCase, level: number): void {
    this.emitCommentsBefore(branch.range.start.offset, level);
    this.printAnnotations(branch, level);
    const header = branch.test === null ? 'default:' : `case "${escapeString(branch.test.text)}":`;
    this.line(level, header);
    this.openBrace(level);
    this.printBlockBody(branch.body, level + 1);
    this.closeBrace(level);
  }

  /* --- block plumbing ------------------------------------------------ */

  private printBlockWithHeader(
    header: string,
    block: Block,
    level: number,
    extra: { trailer?: string; suppressTrailingNewline?: boolean } = {},
  ): void {
    if (this.options.braceStyle === 'same-line') {
      this.line(level, `${header} {`);
    } else {
      this.line(level, header);
      this.line(level, '{');
    }

    this.printBlockBody(block, level + 1);

    if (extra.trailer) {
      this.line(level, `} ${extra.trailer}`);
    } else {
      this.line(level, '}');
    }
  }

  private openBrace(level: number): void {
    this.line(level, '{');
  }

  private closeBrace(level: number): void {
    this.line(level, '}');
  }

  private printBlockBody(block: Block, level: number): void {
    block.statements.forEach((statement, index) => {
      if (index > 0 && isCompound(block.statements[index - 1]!)) this.blank();
      this.printStatement(statement, level);
    });
    // Comments sitting at the very end of a block, after the last statement.
    this.emitCommentsBefore(block.range.end.offset, level);
  }
}

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

function isCompound(statement: TopLevelStatement): boolean {
  return (
    statement.kind === 'IfStatement' ||
    statement.kind === 'WhileStatement' ||
    statement.kind === 'RepeatStatement' ||
    statement.kind === 'ForEachStatement' ||
    statement.kind === 'SwitchStatement' ||
    statement.kind === 'FunctionDeclaration'
  );
}

function branchSuffix(labels: { yes: Label | null; no: Label | null }): string {
  const parts: string[] = [];
  if (labels.yes) parts.push(`yes "${escapeString(labels.yes.text)}"`);
  if (labels.no) parts.push(`no "${escapeString(labels.no.text)}"`);
  return parts.length > 0 ? ` ${parts.join(' ')}` : '';
}

function escapeString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}
